import type {
  AlgoliaHttpResponse,
  Headers,
  Host,
  QueryParameters,
  Request,
  RequestOptions,
  Response,
  StackFrame,
} from '../types';
import { ApiError, DeserializationError, DetailedApiError } from './errors';

export function shuffle<TData>(array: TData[]): TData[] {
  const shuffledArray = array;

  for (let c = array.length - 1; c > 0; c--) {
    const b = Math.floor(Math.random() * (c + 1));
    const a = array[c];

    shuffledArray[c] = array[b];
    shuffledArray[b] = a;
  }

  return shuffledArray;
}

export function serializeUrl(host: Host, path: string, queryParameters: QueryParameters): string {
  const queryParametersAsString = serializeQueryParameters(queryParameters);
  let url = `${host.protocol}://${host.url}${host.port ? `:${host.port}` : ''}/${
    path.charAt(0) === '/' ? path.substring(1) : path
  }`;

  if (queryParametersAsString.length) {
    url += `?${queryParametersAsString}`;
  }

  return url;
}

export function serializeQueryParameters(parameters: QueryParameters): string {
  return Object.keys(parameters)
    .filter((key) => parameters[key] !== undefined)
    .sort()
    .map(
      (key) =>
        `${key}=${encodeURIComponent(
          Object.prototype.toString.call(parameters[key]) === '[object Array]'
            ? parameters[key].join(',')
            : parameters[key],
        ).replace(/\+/g, '%20')}`,
    )
    .join('&');
}

export function serializeData(request: Request, requestOptions: RequestOptions): string | undefined {
  if (request.method === 'GET' || (request.data === undefined && requestOptions.data === undefined)) {
    return undefined;
  }

  const data = Array.isArray(request.data) ? request.data : { ...request.data, ...requestOptions.data };

  return JSON.stringify(data);
}

export function serializeHeaders(
  baseHeaders: Headers,
  requestHeaders: Headers,
  requestOptionsHeaders?: Headers | undefined,
): Headers {
  const headers: Headers = {
    Accept: 'application/json',
    ...baseHeaders,
    ...requestHeaders,
    ...requestOptionsHeaders,
  };
  const serializedHeaders: Headers = {};

  Object.keys(headers).forEach((header) => {
    const value = headers[header];
    serializedHeaders[header.toLowerCase()] = value;
  });

  return serializedHeaders;
}

export function deserializeSuccess<TObject>(response: Response): TObject {
  // Handle 204 No Content and other empty responses
  if (response.status === 204 || response.content.length === 0) {
    return undefined as unknown as TObject;
  }

  try {
    return JSON.parse(response.content);
  } catch (e) {
    throw new DeserializationError((e as Error).message, response, getCorrelationId(response.headers));
  }
}

export function deserializeSuccessWithHttpInfo<TData>(response: Response): AlgoliaHttpResponse<TData> {
  return {
    status: response.status,
    headers: response.headers,
    content: response.content,
    data: deserializeSuccess<TData>(response),
  };
}

export function getCorrelationId(headers: Headers | undefined): string | undefined {
  if (!headers) {
    return undefined;
  }

  if (headers['correlation-id'] !== undefined) {
    return headers['correlation-id'];
  }

  const header = Object.keys(headers).find((key) => key.toLowerCase() === 'correlation-id');

  return header === undefined ? undefined : headers[header];
}

export function getLastCorrelationId(stackTrace: StackFrame[]): string | undefined {
  for (let i = stackTrace.length - 1; i >= 0; i--) {
    const correlationId = getCorrelationId(stackTrace[i].response.headers);
    if (correlationId !== undefined) {
      return correlationId;
    }
  }

  return undefined;
}

export function deserializeFailure(response: Response, stackFrame: StackFrame[]): Error {
  const { content, status } = response;
  const correlationId = getCorrelationId(response.headers);
  try {
    const parsed = JSON.parse(content);
    if ('error' in parsed) {
      return new DetailedApiError(parsed.message, status, parsed.error, stackFrame, correlationId);
    }
    return new ApiError(parsed.message, status, stackFrame, 'ApiError', correlationId);
  } catch {
    // ..
  }
  return new ApiError(content, status, stackFrame, 'ApiError', correlationId);
}
